import PropTypes from "prop-types";
import { MathJax } from "better-react-mathjax";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import RatingComment from "./RatingComment";
import RatingPicker from "./RatingPicker";

import { gradeTranslation } from "../../../../utils/SchoolAdmin/translations";
import { byOrdinal, getColorClass, RATING_DISPLAY_LABELS, stripHtml } from "../../../../utils/teacher/InputRatingUtils";

import "../../../../styles/RatingsList.scss";

// Shared renderer for the GLE label used in both diff and template content sections.
const GLELabel = ({ gleName }) => (
    <p style={{ margin: 0 }}>
        <span dangerouslySetInnerHTML={{ __html: gleName }} />
    </p>
);

GLELabel.propTypes = { gleName: PropTypes.string };
GLELabel.defaultProps = { gleName: "" };

const RatingsList = ({
    canEdit,
    contentItems,
    diffItemsByGrade,
    incomingTemplateId,
    isActiveAndIsUnreleased,
    valuesByKey,
    commentsByKey,
    onChangeRatingByKey,
    commentVisible,
    onChangeCommentByKey,
    onToggleComment,
    schoolLevelExpectations,
    studentProfileDataGrade,
    sleValuesByKey,
    onChangeSleRatingByKey,
    previousRatingsByBodyText,
    previousDiffRatingsByContentId,
    previousSleRatingsByKey,
    hasPreviousSemester
}) => {
    const sortedContentItems = [...contentItems].sort((a, b) => {
        const aRating = valuesByKey?.[`C:${a.templateContentId}`];
        const bRating = valuesByKey?.[`C:${b.templateContentId}`];

        // "EXCLUDED" sorts after everything else
        const aIsExcluded = aRating === "EXCLUDED";
        const bIsExcluded = bRating === "EXCLUDED";

        if (aIsExcluded === bIsExcluded) return 0;
        return aIsExcluded ? 1 : -1; // put excluded later
    });

    const toStableGradeName = (g) => gradeTranslation[g] || g;

    if (incomingTemplateId === "SLE" && schoolLevelExpectations.length > 0) {
        return (
            <div style={{ marginTop: 16 }}>
                {schoolLevelExpectations.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        {schoolLevelExpectations.map((sle) => {
                            const topKey = `SLE:${sle.key}`;
                            const topVal = sleValuesByKey?.[topKey] ?? "";
                            const previousTopRating = hasPreviousSemester ? previousSleRatingsByKey?.[topKey] : null;

                            return (
                                <div key={sle.key} className="item-card">
                                    <div className="plo-wrapper">
                                        <div className="plo-section">
                                            <div style={{ flex: 1, display: "flex" }}>
                                                <p style={{ fontWeight: 600, marginBottom: 8 }}>{sle.expectation}</p>
                                            </div>

                                            <div className="picker-wrapper">
                                                <RatingPicker
                                                    canEdit={canEdit && isActiveAndIsUnreleased}
                                                    value={topVal}
                                                    allowUnrated={false}
                                                    onChange={(nextVal) => onChangeSleRatingByKey(topKey, nextVal)}
                                                />
                                                {previousTopRating && (
                                                    <div
                                                        className={`previous-semester-rating ${getColorClass(previousTopRating)}`}
                                                    >
                                                        <span>
                                                            <strong>Semester 1:</strong>{" "}
                                                            {RATING_DISPLAY_LABELS[previousTopRating] ||
                                                                previousTopRating}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {/* Child expectations */}
                                        {Array.isArray(sle.sleChildList) && sle.sleChildList.length > 0 && (
                                            <div style={{ marginTop: 12 }}>
                                                {[...sle.sleChildList].sort(byOrdinal).map((child) => {
                                                    const childKey = `SLEC:${child.key}`;
                                                    const childVal = sleValuesByKey?.[childKey] ?? "";
                                                    const previousChildRating = hasPreviousSemester
                                                        ? previousSleRatingsByKey?.[childKey]
                                                        : null;

                                                    return (
                                                        <div key={child.key} className="sle-child-card">
                                                            <div style={{ flex: 1 }}>
                                                                <span style={{ fontWeight: 500 }}>
                                                                    {child.expectation}
                                                                </span>
                                                            </div>

                                                            <div className="picker-wrapper">
                                                                <RatingPicker
                                                                    canEdit={canEdit && isActiveAndIsUnreleased}
                                                                    value={childVal}
                                                                    allowUnrated={false}
                                                                    onChange={(nextVal) =>
                                                                        onChangeSleRatingByKey(childKey, nextVal)
                                                                    }
                                                                />
                                                                {previousChildRating && (
                                                                    <div
                                                                        className={`previous-semester-rating ${getColorClass(previousChildRating)}`}
                                                                    >
                                                                        <span>
                                                                            <strong>Semester 1:</strong>{" "}
                                                                            {RATING_DISPLAY_LABELS[
                                                                                previousChildRating
                                                                            ] || previousChildRating}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <hr />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={{ marginTop: 16 }}>
            {diffItemsByGrade && Object.keys(diffItemsByGrade).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <h5 className="content-type-section-title">Differentiated Content Items:</h5>
                    {Object.entries(diffItemsByGrade).map(([grade, items]) => (
                        <div key={grade} style={{ marginBottom: 16 }}>
                            <div style={{ display: "flex", alignItems: "center", margin: "4px 0 8px" }}>
                                <span className="grade-label">
                                    {grade === "K" ? "Kindergarten" : `${toStableGradeName(grade)} Grade`}{" "}
                                </span>
                            </div>

                            {items.map((item) => {
                                const uiKey = item.keyForUI;
                                const value = valuesByKey?.[uiKey] || "";
                                const commentVal = commentsByKey?.[uiKey] ?? "";

                                // Match previous semester diff rating by contentId and grade
                                let previousDiffRatingData = null;
                                if (hasPreviousSemester && previousDiffRatingsByContentId && item.__diffMeta) {
                                    const lookupKey = `${item.__diffMeta.contentId}:${item.__diffMeta.grade}`;
                                    previousDiffRatingData = previousDiffRatingsByContentId[lookupKey];
                                }

                                const previousDiffRating = previousDiffRatingData?.rating;
                                const previousDiffComment = previousDiffRatingData?.comment;

                                return (
                                    <div key={item.templateContentId} className="item-card">
                                        <div className="hierarchy-text-wrapper">
                                            <span className="hierarchy-text">Standard:</span>
                                            <span dangerouslySetInnerHTML={{ __html: item.standardName }} />
                                        </div>
                                        {item.contentType !== "GLE" && (
                                            <div className="hierarchy-text-wrapper">
                                                <span className="hierarchy-text">GLE:</span>
                                                <span dangerouslySetInnerHTML={{ __html: item.gleName }} />
                                            </div>
                                        )}
                                        {item.contentType === "EVIDENCE_OUTCOME_CHILD" && (
                                            <div className="hierarchy-text-wrapper">
                                                <span className="hierarchy-text">Parent Evidence Outcome:</span>
                                                <span dangerouslySetInnerHTML={{ __html: item.eoName }} />
                                            </div>
                                        )}
                                        <hr />
                                        <div className="plo-wrapper">
                                            <div className="plo-section">
                                                <div style={{ flex: 1, display: "flex" }}>
                                                    <span className="plo-title">
                                                        {item.contentType === "GLE" ? "GLE:" : "PLO:"}
                                                    </span>
                                                    <MathJax hideUntilTypeset="first" tabIndex={-1}>
                                                        {item.contentType === "GLE" ? (
                                                            <GLELabel gleName={item.gleName} />
                                                        ) : (
                                                            <p
                                                                dangerouslySetInnerHTML={{ __html: item.label }}
                                                                style={{ margin: 0 }}
                                                            />
                                                        )}
                                                    </MathJax>
                                                </div>

                                                <div className="picker-wrapper">
                                                    <RatingPicker
                                                        canEdit={canEdit && isActiveAndIsUnreleased}
                                                        value={value}
                                                        allowUnrated={true}
                                                        onChange={(nextVal) => {
                                                            onChangeRatingByKey(uiKey, nextVal);
                                                            if (nextVal === "UNRATED" && commentVal)
                                                                onChangeCommentByKey(uiKey, "");
                                                        }}
                                                    />
                                                    {previousDiffRating && previousDiffRating !== "UNRATED" && (
                                                        <div
                                                            className={`previous-semester-rating ${getColorClass(previousDiffRating)}`}
                                                        >
                                                            <span>
                                                                <strong>Semester 1:</strong>{" "}
                                                                {RATING_DISPLAY_LABELS[previousDiffRating] ||
                                                                    previousDiffRating}
                                                            </span>
                                                            {previousDiffComment && (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip
                                                                            id={`tooltip-diff-${item.__diffMeta?.contentId}`}
                                                                        >
                                                                            {previousDiffComment}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i className="bi bi-chat-left-text comment-icon" />
                                                                </OverlayTrigger>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <RatingComment
                                                canEdit={canEdit && isActiveAndIsUnreleased}
                                                id={item?.__diffMeta?.contentId}
                                                isOpen={!!commentVisible[item?.__diffMeta?.contentId]}
                                                onToggle={() => onToggleComment(item?.__diffMeta?.contentId)}
                                                commentValue={commentVal}
                                                onChange={(val) => onChangeCommentByKey(uiKey, val)}
                                                rating={value}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    <hr />
                </div>
            )}
            <div>
                <h5 className="content-type-section-title">
                    Template Content Items -{" "}
                    {studentProfileDataGrade === "K" ? "Kindergarten" : `${studentProfileDataGrade} Grade`}:
                </h5>
            </div>
            {sortedContentItems.map((item) => {
                const uiKey = `C:${item.templateContentId}`;
                const value = valuesByKey?.[uiKey] || "";

                // Match previous semester rating by body text
                let previousRatingData = null;
                if (hasPreviousSemester && previousRatingsByBodyText) {
                    const lookupKey = `${item.contentType}:${stripHtml(item.label)}`;
                    previousRatingData = previousRatingsByBodyText[lookupKey];
                }

                const previousRating = previousRatingData?.rating;
                const previousComment = previousRatingData?.comment;

                return (
                    <div key={item.templateContentId} className="item-card">
                        {item.standardName && item.contentType !== "CDESTANDARD" && (
                            <div className="hierarchy-text-wrapper">
                                <span className="hierarchy-text">Standard:</span>
                                <span dangerouslySetInnerHTML={{ __html: item.standardName }} />
                            </div>
                        )}
                        {item.gleName && item.contentType !== "GLE" && (
                            <div className="hierarchy-text-wrapper">
                                <span className="hierarchy-text">GLE:</span>
                                <span dangerouslySetInnerHTML={{ __html: item.gleName }} />
                            </div>
                        )}
                        {item.contentType === "EVIDENCE_OUTCOME_CHILD" && (
                            <div className="hierarchy-text-wrapper">
                                <span className="hierarchy-text">Parent Evidence Outcome:</span>
                                <span dangerouslySetInnerHTML={{ __html: item.eoName }} />
                            </div>
                        )}
                        <hr />
                        <div className="plo-wrapper">
                            <div className="plo-section">
                                <div style={{ flex: 1, display: "flex" }}>
                                    <span className="plo-title">{item.contentType === "GLE" ? "GLE:" : "PLO:"}</span>
                                    <MathJax hideUntilTypeset="first" tabIndex={-1}>
                                        {item.contentType === "GLE" ? (
                                            <GLELabel gleName={item.gleName} />
                                        ) : (
                                            <p dangerouslySetInnerHTML={{ __html: item.label }} style={{ margin: 0 }} />
                                        )}
                                    </MathJax>
                                </div>

                                <div className="picker-wrapper">
                                    <RatingPicker
                                        canEdit={canEdit && isActiveAndIsUnreleased}
                                        value={value}
                                        allowUnrated={false}
                                        onChange={(nextVal) => onChangeRatingByKey(uiKey, nextVal)}
                                    />
                                    {previousRating && (
                                        <div className={`previous-semester-rating ${getColorClass(previousRating)}`}>
                                            <span>
                                                <strong>Semester 1:</strong>{" "}
                                                {RATING_DISPLAY_LABELS[previousRating] || previousRating}
                                            </span>
                                            {previousComment && (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`tooltip-${item.templateContentId}`}>
                                                            {previousComment}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <i className="bi bi-chat-left-text comment-icon" />
                                                </OverlayTrigger>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <RatingComment
                                canEdit={canEdit && isActiveAndIsUnreleased}
                                id={item.templateContentId}
                                isOpen={!!commentVisible[item.templateContentId]}
                                onToggle={() => onToggleComment(item.templateContentId)}
                                commentValue={commentsByKey[uiKey] || ""}
                                onChange={(val) => onChangeCommentByKey(uiKey, val)}
                                rating={value}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

RatingsList.propTypes = {
    canEdit: PropTypes.bool,
    contentItems: PropTypes.arrayOf(
        PropTypes.shape({
            templateContentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            contentType: PropTypes.string,
            label: PropTypes.string.isRequired,
            standardName: PropTypes.string,
            gleName: PropTypes.string,
            eoName: PropTypes.string
        })
    ).isRequired,
    diffItemsByGrade: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                keyForUI: PropTypes.string.isRequired,
                templateContentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                contentType: PropTypes.string,
                label: PropTypes.string,
                standardName: PropTypes.string,
                gleName: PropTypes.string,
                eoName: PropTypes.string,
                __diffMeta: PropTypes.shape({
                    contentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    grade: PropTypes.string.isRequired
                }).isRequired
            })
        )
    ),
    incomingTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isActiveAndIsUnreleased: PropTypes.bool,
    valuesByKey: PropTypes.objectOf(PropTypes.string),
    commentsByKey: PropTypes.objectOf(PropTypes.string),
    commentVisible: PropTypes.objectOf(PropTypes.bool).isRequired,
    onChangeRatingByKey: PropTypes.func.isRequired,
    onChangeCommentByKey: PropTypes.func.isRequired,
    onToggleComment: PropTypes.func.isRequired,
    schoolLevelExpectations: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            expectation: PropTypes.string.isRequired,
            sleChildList: PropTypes.arrayOf(
                PropTypes.shape({
                    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    expectation: PropTypes.string.isRequired
                })
            )
        })
    ),
    studentProfileDataGrade: PropTypes.string,
    sleValuesByKey: PropTypes.objectOf(PropTypes.string),
    onChangeSleRatingByKey: PropTypes.func,
    previousRatingsByBodyText: PropTypes.objectOf(
        PropTypes.shape({
            rating: PropTypes.string,
            comment: PropTypes.string
        })
    ),
    previousDiffRatingsByContentId: PropTypes.objectOf(
        PropTypes.shape({
            rating: PropTypes.string,
            comment: PropTypes.string
        })
    ),
    previousSleRatingsByKey: PropTypes.objectOf(PropTypes.string),
    hasPreviousSemester: PropTypes.bool
};

RatingsList.defaultProps = {
    canEdit: false,
    diffItemsByGrade: {},
    isActiveAndIsUnreleased: false,
    valuesByKey: {},
    commentsByKey: {},
    studentProfileDataGrade: "Current",
    sleValuesByKey: {},
    onChangeSleRatingByKey: () => {},
    previousRatingsByBodyText: {},
    previousDiffRatingsByContentId: {},
    previousSleRatingsByKey: {},
    hasPreviousSemester: false
};

export default RatingsList;
